import React, { useEffect, useRef, useState } from "react";
import { GripVertical, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { moved, useDragReorder } from "@/lib/useDragReorder";
import { fieldFilterLabel, type FieldFilterValue, type FilterFieldDef } from "@/lib/useFieldFilters";
import { ConditionEditor, FieldPicker, type PickerItem } from "./AddFilter";

/** A quick filter entry is the name of one of the page's own filters ("owner") or "f:<field>" for any field of the list */
export const fieldEntry = (key: string) => `f:${key}`;
export const isFieldEntry = (entry: string) => entry.startsWith("f:");
export const fieldKeyOf = (entry: string) => entry.slice(2);

/** Closes a popover on an outside click or Escape */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && close();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

const popoverCls =
  "z-50 w-72 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl animate-in fade-in-50 zoom-in-95 duration-100 max-sm:fixed max-sm:inset-x-4 max-sm:bottom-4 max-sm:w-auto sm:absolute sm:left-0 sm:top-full sm:mt-1.5";

/**
 * A quick filter on any field: `City: any` until a condition is set (click → condition and value), then a chip
 * `City is Chennai ×`. The value is one of the page's added filters, found by its field.
 */
export function QuickFieldPill({
  def,
  filter,
  onApply,
  onClear,
  suggestUrl,
}: {
  def: FilterFieldDef;
  filter: FieldFilterValue | undefined;
  onApply: (f: FieldFilterValue) => void;
  onClear: () => void;
  suggestUrl?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      {filter ? (
        <span className={cn("inline-flex h-7 max-w-[280px] items-center gap-1 rounded-md bg-accent pl-2.5 pr-1 text-xs font-medium text-accent-foreground", open && "ring-2 ring-primary/40")}>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} title={fieldFilterLabel(def, filter)} className="min-w-0 cursor-pointer truncate">
            {fieldFilterLabel(def, filter)}
          </button>
          <button type="button" onClick={onClear} aria-label={`Remove ${def.label} filter`} className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded hover:bg-primary/15">
            <X className="size-3" />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className={cn("inline-flex h-7 cursor-pointer items-center rounded-md border border-dashed border-border bg-background px-2.5 text-xs text-foreground/80 transition-colors hover:bg-muted", open && "border-primary/50 ring-2 ring-primary/30")}
        >
          {def.label}: {def.type === "date" ? "any time" : "any"}
        </button>
      )}
      {open && (
        <div className={popoverCls}>
          <ConditionEditor
            field={def}
            initial={filter}
            suggestUrl={suggestUrl}
            onApply={(f) => {
              onApply(f);
              setOpen(false);
            }}
            onCancel={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

/** The round "+" after the quick filters: "Add a quick filter", a searchable list of every field (or standard filter) not yet on the row */
export function AddQuickFilter({ items, used, disabled, onAdd }: { items: PickerItem[]; used: Set<string>; disabled: boolean; onAdd: (entry: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={disabled ? "The most quick filters you can keep is reached" : "Add a quick filter"}
        aria-label="Add a quick filter"
        aria-expanded={open}
        className={cn("flex size-7 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-foreground/80 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50", open && "ring-2 ring-primary/30")}
      >
        <Plus className="size-3.5" />
      </button>
      {open && (
        <div className={popoverCls}>
          <FieldPicker
            title="Add a quick filter"
            fields={items}
            used={used}
            onPick={(item) => {
              onAdd(item.key);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** The pencil: "Edit quick filters" — change which fields are on the row, up to the limit */
export function EditQuickFilters({
  items,
  list,
  defaults,
  max,
  onSave,
}: {
  /** Every choice: key is the page's own filter name, or "f:<field>" */
  items: PickerItem[];
  list: string[];
  defaults: string[];
  max: number;
  onSave: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(list);
  const byKey = new Map(items.map((i) => [i.key, i]));
  const groups = items.reduce<Record<string, PickerItem[]>>((acc, i) => ((acc[i.group] ||= []).push(i), acc), {});
  const unused = items.find((i) => !draft.includes(i.key));
  const reorder = useDragReorder(draft.length, (from, to) => setDraft((d) => moved(d, from, to)));

  const start = () => {
    setDraft(list.filter((k) => byKey.has(k)));
    setOpen(true);
  };

  return (
    <>
      <button type="button" onClick={start} aria-label="Edit quick filters" title="Edit quick filters" className="flex size-7 cursor-pointer items-center justify-center rounded-full border border-border bg-background text-foreground/80 transition-colors hover:bg-muted">
        <Pencil className="size-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit quick filters</DialogTitle>
          </DialogHeader>
          <div className="space-y-2.5">
            <p className="text-xs text-muted-foreground">
              Quick filters used: {draft.length}/{max}
            </p>
            <p className="text-[11px] text-muted-foreground">Drag a row by its grip to reorder the quick filters.</p>
            <div className="max-h-[min(46vh,320px)] space-y-1.5 overflow-y-auto pr-1">
            {draft.map((entry, i) => (
              <div
                key={`${entry}-${i}`}
                {...reorder.rowProps(i)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border-t-2 border-transparent",
                  reorder.dragging === i && "opacity-40",
                  reorder.over === i && reorder.dragging !== null && reorder.dragging !== i && "border-primary",
                )}
              >
                <button
                  type="button"
                  aria-label={`Move ${byKey.get(entry)?.label || "quick filter"}. Drag, or use the up and down arrow keys`}
                  title="Drag to reorder"
                  {...reorder.handleProps(i)}
                  className="flex size-7 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-1 focus-visible:ring-primary active:cursor-grabbing"
                >
                  <GripVertical className="size-4" />
                </button>
                <select
                  aria-label={`Quick filter ${i + 1}`}
                  value={entry}
                  onChange={(e) => setDraft(draft.map((d, j) => (j === i ? e.target.value : d)))}
                  className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-[13px] outline-none focus:ring-1 focus:ring-primary"
                >
                  {Object.entries(groups).map(([group, rows]) => (
                    <optgroup key={group} label={group}>
                      {rows.map((r) => (
                        <option key={r.key} value={r.key} disabled={r.key !== entry && draft.includes(r.key)}>
                          {r.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <button type="button" onClick={() => setDraft(draft.filter((_, j) => j !== i))} aria-label={`Remove ${byKey.get(entry)?.label || "quick filter"}`} className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-destructive">
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" disabled={draft.length >= max || !unused} onClick={() => unused && setDraft([...draft, unused.key])} className="h-8 gap-1.5 text-xs">
                <Plus className="size-3.5" /> Add quick filter
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!draft.length} onClick={() => setDraft([])} className="h-8 gap-1.5 text-xs">
                <Trash2 className="size-3.5" /> Delete all quick filters
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(defaults.filter((k) => byKey.has(k)))} className="h-8 text-xs">
                Reset to default
              </Button>
            </div>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                onSave(draft);
                setOpen(false);
              }}
            >
              Done
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
